import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useState } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import { Input } from '@/common/ui/input'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Skeleton } from '@/common/ui/skeleton'
import { Edit, Plus, Trash2, Search } from 'lucide-react'
import { AlertModal } from '@/common/components/AlertModal'
import useQueryGetFlexHotGameList from '../../api/hooks/useQueryGetFlexHotGameList'
import useMutationUpdateFlexHotGame from '../../api/hooks/useMutationUpdateFlexHotGame'
import useMutationDeleteFlexHotGame from '../../api/hooks/useMutationDeleteFlexHotGame'
import { settingsKeys } from '../../api/keys'
import FlexMessagePresetGallery from '../../components/FlexMessagePresetGallery'

import type { HotGameFlexMessageModel } from '../../types'

const FlexHotGamesListPage = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [search, setSearch] = useState('')
	const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(
		undefined,
	)
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [selectedFlexGameId, setSelectedFlexGameId] = useState<string | null>(
		null,
	)

	const {
		data: flexHotGamesResponse,
		isLoading,
		refetch,
	} = useQueryGetFlexHotGameList({
		search: search || undefined,
		isActive: isActiveFilter,
	})

	// Extract items from the response
	const flexHotGames = flexHotGamesResponse?.data?.items || []

	const updateFlexHotGameMutation = useMutationUpdateFlexHotGame({
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: settingsKeys.flexHotGames() })
			queryClient.invalidateQueries({
				queryKey: settingsKeys.flexHotGame(variables.id),
			})
			refetch()
		},
	})

	const deleteFlexHotGameMutation = useMutationDeleteFlexHotGame({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: settingsKeys.flexHotGames() })
			toast.success('ลบ Flex Message สำเร็จ')
			setDeleteModalOpen(false)
			setSelectedFlexGameId(null)
			refetch()
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการลบ Flex Message')
		},
	})

	const handleToggleActive = (
		id: string,
		currentStatus: boolean,
		name: string,
	) => {
		updateFlexHotGameMutation.mutate(
			{
				id,
				data: {
					isActive: !currentStatus,
				},
			},
			{
				onSuccess: () => {
					toast.success(
						`${!currentStatus ? 'เปิด' : 'ปิด'}การใช้งาน "${name}" สำเร็จ`,
					)
				},
				onError: () => {
					toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ')
				},
			},
		)
	}

	const handleDelete = () => {
		if (!selectedFlexGameId) return

		deleteFlexHotGameMutation.mutate(selectedFlexGameId)
	}

	return (
		<PageContainer>
			<div className="space-y-4 px-4 sm:px-0">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<Heading
						title="จัดการ Flex Message"
						description="รายการ Flex Message สำหรับส่งโฆษณาเกม"
					/>
					<Button
						onClick={() => navigate('/operator/settings/flex-hot-games/create')}
					>
						<Plus className="mr-2 h-4 w-4" />
						สร้าง Flex Message
					</Button>
				</div>

				<div className="pt-6">
					<FlexMessagePresetGallery />
				</div>

				<Separator className="my-4 sm:my-6" />

				{/* Filters */}
				<div className="flex flex-col gap-4 sm:flex-row">
					<div className="relative flex-1">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="ค้นหาชื่อ Flex Message..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
					<Select
						value={
							isActiveFilter === undefined
								? 'all'
								: isActiveFilter
									? 'active'
									: 'inactive'
						}
						onValueChange={(value) => {
							if (value === 'all') {
								setIsActiveFilter(undefined)
							} else {
								setIsActiveFilter(value === 'active')
							}
						}}
					>
						<SelectTrigger className="w-full sm:w-[180px]">
							<SelectValue placeholder="สถานะ" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">ทั้งหมด</SelectItem>
							<SelectItem value="active">ใช้งาน</SelectItem>
							<SelectItem value="inactive">ปิดการใช้งาน</SelectItem>
						</SelectContent>
					</Select>
				</div>

				{isLoading ? (
					<div className="space-y-3">
						{[...Array(5)].map((_, i) => (
							<Skeleton key={i} className="h-12 w-full" />
						))}
					</div>
				) : (
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>ชื่อ</TableHead>
									<TableHead>คำอธิบาย</TableHead>
									<TableHead>จำนวนเกม</TableHead>
									<TableHead>สถานะ</TableHead>
									<TableHead>วันที่สร้าง</TableHead>
									<TableHead className="text-right">จัดการ</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{flexHotGames && flexHotGames.length > 0 ? (
									flexHotGames.map((flexGame: HotGameFlexMessageModel) => (
										<TableRow key={flexGame.id}>
											<TableCell className="font-medium">
												{flexGame.name}
											</TableCell>
											<TableCell>
												<div className="max-w-xs truncate text-sm text-muted-foreground">
													{flexGame.description || '-'}
												</div>
											</TableCell>
											<TableCell>
												<Badge variant="secondary">
													{flexGame.games?.length || 0} เกม
												</Badge>
											</TableCell>
											<TableCell>
												<Switch
													checked={flexGame.isActive ?? true}
													onCheckedChange={() =>
														handleToggleActive(
															flexGame.id,
															flexGame.isActive ?? true,
															flexGame.name,
														)
													}
													disabled={
														updateFlexHotGameMutation.isPending ||
														deleteFlexHotGameMutation.isPending
													}
												/>
											</TableCell>
											<TableCell>
												{flexGame.createdAt
													? format(
															new Date(flexGame.createdAt),
															'dd MMM yyyy HH:mm',
															{ locale: th },
														)
													: '-'}
											</TableCell>
											<TableCell className="text-right">
												<div className="flex justify-end gap-2">
													<Button
														variant="ghost"
														size="sm"
														onClick={() =>
															navigate(
																`/operator/settings/flex-hot-games/edit/${flexGame.id}`,
															)
														}
													>
														<Edit className="h-4 w-4" />
													</Button>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => {
															setSelectedFlexGameId(flexGame.id)
															setDeleteModalOpen(true)
														}}
														disabled={deleteFlexHotGameMutation.isPending}
													>
														<Trash2 className="h-4 w-4 text-destructive" />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={6} className="text-center">
											<div className="py-8 text-muted-foreground">
												{search || isActiveFilter !== undefined
													? 'ไม่พบข้อมูลที่ค้นหา'
													: 'ยังไม่มี Flex Message'}
											</div>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				)}
			</div>

			<AlertModal
				isOpen={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false)
					setSelectedFlexGameId(null)
				}}
				onConfirm={handleDelete}
				loading={deleteFlexHotGameMutation.isPending}
			/>
		</PageContainer>
	)
}

const GuardedFlexHotGamesListPage = withUserPermission(FlexHotGamesListPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_SMS],
})

export default GuardedFlexHotGamesListPage
